import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer, Mail } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { eur, deDate, reminderLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { EmailSendDialog } from "@/components/EmailSendDialog";

export default function InvoiceReminder() {
  const { readOnly } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const sheetRef = useRef(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const { data: inv, isLoading } = useQuery({ queryKey: ["invoice", id], queryFn: async () => (await api.get(`/invoices/${id}`)).data });
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  if (isLoading || !inv) return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Lädt…</div>;

  const level = inv.reminder_level || 1;
  const title = reminderLabel(level);
  const days = Number(s?.reminder_days ?? 7);
  const deadline = new Date();
  deadline.setDate(deadline.getDate() + days);
  const deadlineISO = deadline.toISOString().slice(0, 10);
  const todayISO = new Date().toISOString().slice(0, 10);

  const senderLine = [s?.company_name, s?.street, (s?.zip || s?.city) ? `${s?.zip || ""} ${s?.city || ""}`.trim() : ""].filter(Boolean).join(" · ");
  const feeText = level >= 2 ? "Für diese Mahnung berechnen wir keine Mahngebühren." : null;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/invoices")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="reminder-back">
          <ArrowLeft className="h-4 w-4" /> Zurück
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(`/invoices/${id}/print`)} className="text-sm px-3.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="reminder-open-invoice">Rechnung</button>
          {!readOnly && (
            <button onClick={() => setEmailOpen(true)} className="text-sm px-3.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="email-reminder-button">
              <Mail className="h-4 w-4" /> Per E-Mail
            </button>
          )}
          <button onClick={() => window.print()} data-testid="reminder-export-pdf" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2">
            <Download className="h-4 w-4" /> Als PDF exportieren
          </button>
        </div>
      </div>

      {/* A4 letter */}
      <div className="py-8 px-4 flex justify-center">
        <div ref={sheetRef} className="print-area bg-white w-full max-w-[210mm] min-h-[297mm] shadow-xl px-10 sm:px-14 py-12 text-slate-900 flex flex-col" data-testid="reminder-sheet">
          {/* Logo / sender */}
          <div className="flex items-start justify-between gap-6">
            <div>
              {s?.logo_url ? (
                <img src={s.logo_url} alt="Logo" className="h-12 object-contain" />
              ) : (
                <div className="font-display text-2xl font-extrabold tracking-tight">{s?.company_name || "Ihre Firma"}</div>
              )}
            </div>
            <div className="text-right text-xs text-slate-500 leading-relaxed">
              {s?.company_name && <div className="font-semibold text-slate-700">{s.company_name}</div>}
              {s?.owner_name && <div>{s.owner_name}</div>}
              {s?.street && <div>{s.street}</div>}
              {(s?.zip || s?.city) && <div>{s?.zip} {s?.city}</div>}
              {s?.phone && <div>{s.phone}</div>}
              {s?.email && <div>{s.email}</div>}
            </div>
          </div>

          {/* Sender micro-line + recipient */}
          <div className="mt-12">
            {senderLine && <div className="text-[10px] text-slate-400 border-b border-slate-200 pb-1 mb-4">{senderLine}</div>}
            <div className="text-sm leading-relaxed">
              {inv.customer?.company_name && <div className="font-semibold">{inv.customer.company_name}</div>}
              {inv.customer?.contact && <div>{inv.customer.contact}</div>}
              {inv.customer?.street && <div>{inv.customer.street}</div>}
              {(inv.customer?.zip || inv.customer?.city) && <div>{inv.customer?.zip} {inv.customer?.city}</div>}
            </div>
          </div>

          {/* Place & date */}
          <div className="mt-8 text-sm text-right text-slate-600">
            {s?.city ? `${s.city}, den ` : "Datum: "}{deDate(todayISO)}
          </div>

          {/* Subject */}
          <h1 className="mt-8 font-display text-xl font-bold" data-testid="reminder-title">{title} zu Rechnung {inv.number}</h1>

          {/* Body */}
          <div className="mt-5 text-sm leading-relaxed space-y-4 text-slate-700">
            <p>Sehr geehrte Damen und Herren,</p>
            <p>
              zu unserer Rechnung <span className="font-medium">{inv.number}</span> vom {deDate(inv.invoice_date)} über{" "}
              <span className="font-medium">{eur(inv.gross)}</span> konnten wir bis heute leider keinen Zahlungseingang feststellen.
              Die Rechnung war {inv.due_date ? <>am <span className="font-medium">{deDate(inv.due_date)}</span> </> : ""}fällig.
            </p>
            <p>
              Wir bitten Sie, den offenen Betrag von <span className="font-medium">{eur(inv.gross)}</span> bis spätestens{" "}
              <span className="font-medium">{deDate(deadlineISO)}</span> auf das unten genannte Konto zu überweisen.
            </p>
            {feeText && <p>{feeText}</p>}
            <p>Sollten Sie die Zahlung zwischenzeitlich bereits veranlasst haben, betrachten Sie dieses Schreiben bitte als gegenstandslos.</p>
            <p>Mit freundlichen Grüßen<br />{s?.owner_name || s?.company_name}</p>
          </div>

          {/* Summary table */}
          <table className="w-full mt-8 text-sm border-t-2 border-blue-700">
            <tbody>
              <tr className="border-b border-slate-100"><td className="py-2 text-slate-500">Rechnungsnummer</td><td className="py-2 text-right font-mono">{inv.number}</td></tr>
              <tr className="border-b border-slate-100"><td className="py-2 text-slate-500">Rechnungsdatum</td><td className="py-2 text-right">{deDate(inv.invoice_date)}</td></tr>
              {inv.due_date && <tr className="border-b border-slate-100"><td className="py-2 text-slate-500">Ursprüngliche Fälligkeit</td><td className="py-2 text-right">{deDate(inv.due_date)}</td></tr>}
              <tr className="border-b border-slate-100"><td className="py-2 text-slate-500">Neue Zahlungsfrist</td><td className="py-2 text-right font-medium">{deDate(deadlineISO)}</td></tr>
              <tr><td className="py-2 font-semibold">Offener Betrag</td><td className="py-2 text-right font-mono font-bold" data-testid="reminder-amount">{eur(inv.gross)}</td></tr>
            </tbody>
          </table>

          {/* Footer bank details */}
          <div className="mt-auto pt-10">
            <div className="grid grid-cols-3 gap-6 text-[10px] text-slate-400 leading-relaxed border-t border-slate-200 pt-6">
              <div>
                <div className="font-semibold text-slate-500 mb-1">Kontakt</div>
                {s?.company_name && <div>{s.company_name}</div>}
                {s?.phone && <div>{s.phone}</div>}
                {s?.email && <div>{s.email}</div>}
              </div>
              <div>
                <div className="font-semibold text-slate-500 mb-1">Steuer</div>
                {s?.tax_number && <div>Steuernr.: {s.tax_number}</div>}
                {s?.vat_id && <div>USt-IdNr.: {s.vat_id}</div>}
              </div>
              <div>
                <div className="font-semibold text-slate-500 mb-1">Bankverbindung</div>
                {s?.bank_name && <div>{s.bank_name}</div>}
                {s?.iban && <div>IBAN: {s.iban}</div>}
                {s?.bic && <div>BIC: {s.bic}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="no-print text-center pb-8 text-xs text-slate-400 inline-flex items-center gap-1.5 w-full justify-center">
        <Printer className="h-3.5 w-3.5" /> Tipp: Im Druckdialog „Als PDF speichern" wählen.
      </div>

      {emailOpen && <EmailSendDialog invoiceId={id} docType="reminder" number={inv.number} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}
